import { 
  doc, 
  getDoc,
  updateDoc,
  Timestamp,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { db } from './firebase';

// Types
export interface SavedListing {
  businessId: string;
  notes?: string;
  tags?: string[];
  savedAt: Timestamp;
}

export interface SavedListingWithBusiness extends SavedListing {
  business?: any; // BusinessListing data
}

// Saved Listings Service Class
class SavedListingsService {
  private collection = 'users';

  // Save a listing for a user
  async saveListing(userId: string, businessId: string, notes?: string, tags?: string[]): Promise<void> {
    try {
      const userDocRef = doc(db, this.collection, userId);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      const userData = userDoc.data();
      const savedListings = userData.savedListings || [];

      // Check if listing is already saved
      const existingIndex = savedListings.findIndex((sl: SavedListing) => sl.businessId === businessId);
      
      if (existingIndex >= 0) {
        // Update existing saved listing
        const updatedListings = [...savedListings];
        updatedListings[existingIndex] = {
          businessId,
          notes: notes || updatedListings[existingIndex].notes || '',
          tags: tags || updatedListings[existingIndex].tags || [],
          savedAt: Timestamp.now()
        };
        
        await updateDoc(userDocRef, {
          savedListings: updatedListings,
          'stats.savedListingsCount': updatedListings.length,
          'profile.lastActive': Timestamp.now()
        });
      } else {
        // Add new saved listing
        const newSavedListing: SavedListing = {
          businessId,
          notes: notes || '',
          tags: tags || [],
          savedAt: Timestamp.now()
        };
        
        await updateDoc(userDocRef, {
          savedListings: arrayUnion(newSavedListing),
          'stats.savedListingsCount': savedListings.length + 1,
          'profile.lastActive': Timestamp.now()
        });
      }
    } catch (error) {
      console.error('Error saving listing:', error);
      throw error;
    }
  }

  // Get a specific saved listing by userId and businessId
  async getSavedListing(userId: string, businessId: string): Promise<SavedListing | null> {
    try {
      const userDocRef = doc(db, this.collection, userId);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        return null;
      }

      const userData = userDoc.data();
      const savedListings = userData.savedListings || [];
      const savedListing = savedListings.find((sl: SavedListing) => sl.businessId === businessId);
      
      return savedListing || null;
    } catch (error) {
      console.error('Error getting saved listing:', error);
      throw error;
    }
  }

  // Check if a listing is saved by a user
  async isListingSaved(userId: string, businessId: string): Promise<boolean> {
    try {
      const savedListing = await this.getSavedListing(userId, businessId);
      return savedListing !== null;
    } catch (error) {
      console.error('Error checking if listing is saved:', error);
      return false;
    }
  }

  // Get all saved listings for a user
  async getSavedListings(userId: string): Promise<SavedListing[]> {
    try {
      const userDocRef = doc(db, this.collection, userId);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        return [];
      }

      const userData = userDoc.data();
      const savedListings = userData.savedListings || [];
      
      // Sort by savedAt descending
      return savedListings.sort((a: SavedListing, b: SavedListing) => {
        const aTime = a.savedAt?.toMillis?.() || 0;
        const bTime = b.savedAt?.toMillis?.() || 0;
        return bTime - aTime;
      });
    } catch (error) {
      console.error('Error getting saved listings:', error);
      throw error;
    }
  }

  // Update a saved listing (notes, tags) by businessId
  async updateSavedListing(userId: string, businessId: string, updates: { notes?: string; tags?: string[] }): Promise<void> {
    try {
      const userDocRef = doc(db, this.collection, userId);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      const userData = userDoc.data();
      const savedListings = userData.savedListings || [];
      const existingIndex = savedListings.findIndex((sl: SavedListing) => sl.businessId === businessId);
      
      if (existingIndex < 0) {
        throw new Error('Saved listing not found');
      }

      const updatedListings = [...savedListings];
      updatedListings[existingIndex] = {
        ...updatedListings[existingIndex],
        ...updates
      };
      
      await updateDoc(userDocRef, {
        savedListings: updatedListings,
        'profile.lastActive': Timestamp.now()
      });
    } catch (error) {
      console.error('Error updating saved listing:', error);
      throw error;
    }
  }

  // Remove a saved listing
  async removeSavedListing(userId: string, businessId: string): Promise<void> {
    try {
      const userDocRef = doc(db, this.collection, userId);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        return;
      }

      const userData = userDoc.data();
      const savedListings = userData.savedListings || [];
      const savedListing = savedListings.find((sl: SavedListing) => sl.businessId === businessId);
      
      if (savedListing) {
        await updateDoc(userDocRef, {
          savedListings: arrayRemove(savedListing),
          'stats.savedListingsCount': Math.max(0, savedListings.length - 1),
          'profile.lastActive': Timestamp.now()
        });
      }
    } catch (error) {
      console.error('Error removing saved listing:', error);
      throw error;
    }
  }

  // Remove a saved listing by businessId (alias for consistency)
  async removeSavedListingById(userId: string, businessId: string): Promise<void> {
    return this.removeSavedListing(userId, businessId);
  }

  // Get count of saved listings for a user
  async getSavedListingsCount(userId: string): Promise<number> {
    try {
      const savedListings = await this.getSavedListings(userId);
      return savedListings.length;
    } catch (error) {
      console.error('Error getting saved listings count:', error);
      return 0;
    }
  }
}

export const savedListingsService = new SavedListingsService();
export default savedListingsService;
